import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useCreateExercisesBulk, useUploadImage } from "../../service/api";
import { Exercise } from "../../types";

type DraftRow = {
  key: string;
  title: string;
  instruction: string;
  durationInSeconds: number;
  videoUrl: string;
  imageUrl: string;
  error?: string;
};

// Order matters: this is the column order used when mapping pasted Excel/TSV data onto rows.
const PASTE_COLUMNS: (keyof Pick<DraftRow, "title" | "instruction" | "durationInSeconds" | "videoUrl">)[] = [
  "title",
  "instruction",
  "durationInSeconds",
  "videoUrl",
];

const INITIAL_ROW_COUNT = 5;

const normalizeHeader = (header: string) =>
  header
    .toLowerCase()
    .replace(/\*/g, "")
    .replace(/[^a-z0-9]/g, "");

const HEADER_ALIASES: Record<
  keyof Pick<DraftRow, "title" | "instruction" | "durationInSeconds" | "videoUrl">,
  string[]
> = {
  title: ["title", "title*"],
  instruction: ["instruction", "instructions", "instruction*"],
  durationInSeconds: ["duration", "durations", "duration(s)", "duration(s)*", "durationseconds"],
  videoUrl: ["videourl", "video", "url", "videourl*"],
};

const isHeaderMatch = (
  header: string,
  field: keyof Pick<DraftRow, "title" | "instruction" | "durationInSeconds" | "videoUrl">
) => HEADER_ALIASES[field].map(normalizeHeader).includes(header);

// Maps a raw spreadsheet row (keyed by its own header text) onto our DraftRow fields,
// matching header names loosely (case/spacing/punctuation-insensitive) so exports like
// "Title", "Title *", "Duration (s)", "duration_s", or "Video URL" all resolve correctly.
const mapImportedRow = (record: Record<string, unknown>) => {
  const mapped: Partial<Pick<DraftRow, "title" | "instruction" | "durationInSeconds" | "videoUrl">> = {};

  Object.entries(record).forEach(([header, rawValue]) => {
    const key = normalizeHeader(header);
    const value = rawValue == null ? "" : String(rawValue).trim();

    if (isHeaderMatch(key, "title")) {
      mapped.title = value;
    } else if (isHeaderMatch(key, "instruction")) {
      mapped.instruction = value;
    } else if (isHeaderMatch(key, "durationInSeconds") || key.includes("duration")) {
      mapped.durationInSeconds = Number(value) || 0;
    } else if (isHeaderMatch(key, "videoUrl") || key.includes("video")) {
      mapped.videoUrl = value;
    }
  });

  return mapped;
};

export const BulkCreateExercisesTable = () => {
  const { t } = useTranslation();
  const rowCounter = useRef(0);
  const uploadTargetRowKey = useRef<string | null>(null);
  const rowImageInputRef = useRef<HTMLInputElement>(null);
  const excelInputRef = useRef<HTMLInputElement>(null);

  const makeEmptyRow = (): DraftRow => {
    rowCounter.current += 1;
    return {
      key: `row-${rowCounter.current}`,
      title: "",
      instruction: "",
      durationInSeconds: 0,
      videoUrl: "",
      imageUrl: "",
    };
  };

  const [rows, setRows] = useState<DraftRow[]>(() =>
    Array.from({ length: INITIAL_ROW_COUNT }, () => makeEmptyRow())
  );
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [uploadingRowKey, setUploadingRowKey] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ success: number; fail: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const { mutate: createExercisesBulk, isPending: submitting } = useCreateExercisesBulk();
  const { mutate: uploadImage } = useUploadImage();

  const isRowEmpty = (row: DraftRow) =>
    !row.title.trim() && !row.instruction.trim() && !row.videoUrl.trim() && !row.durationInSeconds;

  const isRowValid = (row: DraftRow) =>
    !!row.title.trim() && !!row.instruction.trim() && !!row.videoUrl.trim() && row.durationInSeconds > 0;

  const validCount = rows.filter(isRowValid).length;

  const updateRow = (key: string, field: keyof DraftRow, value: string | number) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value, error: undefined } : row)));
  };

  const addRows = (count: number) => {
    setRows((prev) => [...prev, ...Array.from({ length: count }, () => makeEmptyRow())]);
  };

  const duplicateRow = (key: string) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row.key === key);
      if (index === -1) return prev;
      const copy = { ...prev[index], key: makeEmptyRow().key, error: undefined };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
  };

  const removeRow = (key: string) => {
    setRows((prev) => {
      const next = prev.filter((row) => row.key !== key);
      return next.length > 0 ? next : [makeEmptyRow()];
    });
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLDivElement | HTMLInputElement>,
    rowIndex: number,
    colIndex: number
  ) => {
    const text = e.clipboardData.getData("text");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return; // let normal single-value paste happen
    e.preventDefault();

    const pastedRows = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((line, i, arr) => !(i === arr.length - 1 && line === ""))
      .map((line) => line.split("\t"));

    setRows((prev) => {
      const next = [...prev];
      pastedRows.forEach((cells, offset) => {
        const targetIndex = rowIndex + offset;
        while (targetIndex >= next.length) {
          next.push(makeEmptyRow());
        }
        let row = { ...next[targetIndex] };
        cells.forEach((cellValue, cellOffset) => {
          const col = PASTE_COLUMNS[colIndex + cellOffset];
          if (!col) return;
          if (col === "durationInSeconds") {
            row.durationInSeconds = Number(cellValue.trim()) || 0;
          } else {
            row[col] = cellValue.trim();
          }
        });
        row.error = undefined;
        next[targetIndex] = row;
      });
      return next;
    });
  };

  const handleRowImageClick = (key: string) => {
    uploadTargetRowKey.current = key;
    rowImageInputRef.current?.click();
  };

  const handleRowImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetKey = uploadTargetRowKey.current;
    e.target.value = "";
    if (!file || !targetKey) return;

    setUploadingRowKey(targetKey);
    uploadImage(file, {
      onSuccess: (url) => {
        updateRow(targetKey, "imageUrl", url);
        setUploadingRowKey(null);
      },
      onError: () => setUploadingRowKey(null),
    });
  };

  const handleImportClick = () => {
    excelInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImportError(null);
    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) throw new Error("No sheet found");

      const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const importedFields = records
        .map(mapImportedRow)
        .filter((row) => row.title || row.instruction || row.videoUrl || row.durationInSeconds);

      if (importedFields.length === 0) {
        setImportError(t("videoLibrary.bulkCreate.importEmpty"));
        return;
      }

      // Backfill existing empty rows first (in order), only appending new rows once those run out.
      setRows((prev) => {
        const next = [...prev];
        let searchFrom = 0;
        importedFields.forEach((fields) => {
          const slot = next.findIndex((row, index) => index >= searchFrom && isRowEmpty(row));
          if (slot === -1) {
            next.push({ ...makeEmptyRow(), ...fields });
          } else {
            next[slot] = { ...next[slot], ...fields, error: undefined };
            searchFrom = slot + 1;
          }
        });
        return next;
      });
    } catch {
      setImportError(t("videoLibrary.bulkCreate.importError"));
    }
  };

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    const validRows = rows.filter(isRowValid);
    if (validRows.length === 0) return;

    const exercises: Exercise[] = validRows.map((row) => ({
      title: row.title.trim(),
      instruction: row.instruction.trim(),
      durationInSeconds: row.durationInSeconds,
      imageUrl: row.imageUrl,
      videoUrl: row.videoUrl.trim(),
      isDeleted: false,
    }));

    createExercisesBulk(exercises, {
      onSuccess: (results) => {
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.length - successCount;
        setSummary({ success: successCount, fail: failCount });

        const validKeys = validRows.map((row) => row.key);
        const errorByKey = new Map<string, string | undefined>();
        results.forEach((result, i) => {
          if (!result.success) errorByKey.set(validKeys[i], result.error);
        });

        setRows((prev) => {
          const remaining = prev
            .filter((row) => !validKeys.includes(row.key) || errorByKey.has(row.key))
            .map((row) => (errorByKey.has(row.key) ? { ...row, error: errorByKey.get(row.key) } : row));
          return remaining.length > 0 ? remaining : [makeEmptyRow()];
        });
        if (failCount === 0) setAttemptedSubmit(false);
      },
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 2, backgroundColor: "#fff", color: "#000", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <input
        ref={rowImageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleRowImageSelected}
      />
      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={handleImportFile}
      />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexShrink: 0 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">{t("videoLibrary.bulkCreate.heading")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("videoLibrary.bulkCreate.hint")}
          </Typography>
        </Box>
        <Typography variant="body2" color={validCount > 0 ? "primary" : "text.secondary"} fontWeight={600}>
          {t("videoLibrary.bulkCreate.rowsReady", { ready: validCount, total: rows.length })}
        </Typography>
      </Box>

      {importError && (
        <Alert severity="error" onClose={() => setImportError(null)} sx={{ mb: 1 }}>
          {importError}
        </Alert>
      )}

      {summary && (
        <Alert
          severity={summary.fail === 0 ? "success" : "warning"}
          onClose={() => setSummary(null)}
          sx={{ mb: 1 }}
        >
          {t("videoLibrary.bulkCreate.summarySuccess", { count: summary.success })}
          {summary.fail > 0
            ? t("videoLibrary.bulkCreate.summaryFailSuffix", { count: summary.fail })
            : t("videoLibrary.bulkCreate.summaryAllSuccessSuffix")}
        </Alert>
      )}

      <TableContainer sx={{ flex: 1, overflow: "auto", mb: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">{t("videoLibrary.bulkCreate.colIndex")}</TableCell>
              <TableCell sx={{ minWidth: 160 }}>{t("videoLibrary.bulkCreate.colTitle")}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>{t("videoLibrary.bulkCreate.colInstruction")}</TableCell>
              <TableCell sx={{ width: 90 }}>{t("videoLibrary.bulkCreate.colDuration")}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>{t("videoLibrary.bulkCreate.colVideoUrl")}</TableCell>
              <TableCell padding="checkbox">{t("videoLibrary.bulkCreate.colImage")}</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => {
              const showErrors = attemptedSubmit && !isRowEmpty(row);
              return (
                <React.Fragment key={row.key}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <Typography variant="caption" color="text.secondary">{rowIndex + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={row.title}
                      onChange={(e) => updateRow(row.key, "title", e.target.value)}
                      onPaste={(e) => handlePaste(e, rowIndex, 0)}
                      error={showErrors && !row.title.trim()}
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={row.instruction}
                      onChange={(e) => updateRow(row.key, "instruction", e.target.value)}
                      onPaste={(e) => handlePaste(e, rowIndex, 1)}
                      error={showErrors && !row.instruction.trim()}
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={row.durationInSeconds}
                      onChange={(e) => updateRow(row.key, "durationInSeconds", Number(e.target.value))}
                      onFocus={(e) => e.target.select()}
                      onPaste={(e) => handlePaste(e, rowIndex, 2)}
                      error={showErrors && row.durationInSeconds <= 0}
                      size="small"
                      sx={{ width: 70 }}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={row.videoUrl}
                      onChange={(e) => updateRow(row.key, "videoUrl", e.target.value)}
                      onPaste={(e) => handlePaste(e, rowIndex, 3)}
                      error={showErrors && !row.videoUrl.trim()}
                      size="small"
                      fullWidth
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Tooltip title={row.imageUrl ? t("videoLibrary.common.changeImage") : t("videoLibrary.common.uploadImage")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRowImageClick(row.key)}
                          disabled={uploadingRowKey === row.key}
                        >
                          {row.imageUrl ? (
                            <Box component="img" src={row.imageUrl} alt="" sx={{ height: 20, width: 20, objectFit: "cover", borderRadius: 0.5 }} />
                          ) : (
                            <ImageIcon fontSize="small" sx={{ color: uploadingRowKey === row.key ? "#bbb" : "#1976d2" }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title={t("videoLibrary.bulkCreate.duplicateRowTooltip")}>
                        <IconButton size="small" onClick={() => duplicateRow(row.key)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("videoLibrary.bulkCreate.removeRowTooltip")}>
                        <IconButton size="small" onClick={() => removeRow(row.key)} sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                {row.error && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0.5, borderTop: "none" }}>
                      <Typography variant="caption" color="error">{row.error}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addRows(1)}>{t("videoLibrary.bulkCreate.addRow")}</Button>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addRows(5)}>{t("videoLibrary.bulkCreate.addFiveRows")}</Button>
          <Button size="small" startIcon={<UploadFileIcon />} onClick={handleImportClick}>{t("videoLibrary.bulkCreate.importFromExcel")}</Button>
        </Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || validCount === 0}
          sx={{ color: "#fff" }}
        >
          {submitting ? t("videoLibrary.bulkCreate.creating") : t("videoLibrary.bulkCreate.createAll", { count: validCount })}
        </Button>
      </Box>
    </Paper>
  );
};
